import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import VehicleFinder from "@/components/home/VehicleFinder";
import ProductCard from "@/components/product/ProductCard";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronsRight, Car } from "lucide-react";

export default function VehicleFinderPage() {
  const [params] = useSearchParams();
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const year = params.get("year");
  const make = params.get("make");
  const engine = params.get("engine");

  useEffect(() => {
    if (!year || !make) { setItems(null); return; }
    setLoading(true);
    api.get("/vehicles/lookup", { params: { year, make, engine: engine || undefined } })
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  }, [year, make, engine]);

  return (
    <div data-testid="vehicle-finder-page">
      <div className="bg-[#0B0F19] text-white py-12 lg:py-20 relative overflow-hidden">
        <div className="absolute inset-0 ici-grid-bg-dark opacity-40" />
        <div className="max-w-5xl mx-auto px-4 lg:px-8 relative">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#0055FF]"><Car className="inline w-3 h-3 mr-1" /> Vehicle finder</div>
          <h1 className="mt-2 font-display text-3xl lg:text-5xl font-extrabold tracking-tight">Find compatible parts for your vehicle</h1>
          <p className="mt-3 text-slate-400 max-w-2xl">Select your year, make, and engine to see matching components from our current inventory.</p>
          <div className="mt-6"><VehicleFinder variant="hero" /></div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-10">
        {year && make && (
          <>
            <nav className="text-xs font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1.5 mb-6">
              <Link to="/" className="hover:text-[#0055FF]">Home</Link>
              <ChevronsRight className="w-3 h-3" />
              <span className="text-slate-900">{year} {make}{engine ? ` · ${engine}` : ""}</span>
            </nav>
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="aspect-[4/3]" />)}
              </div>
            ) : items && items.length ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {items.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
              </div>
            ) : (
              <div className="text-center py-16 rounded-xl border border-slate-200 bg-slate-50">
                <div className="text-slate-500">No products match this vehicle in our current inventory.</div>
                <Link to="/request-a-part" className="mt-3 inline-block text-[#0055FF] hover:underline">Request this part →</Link>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
